import { Suspense, useEffect, useRef } from 'react'
import { MathUtils } from 'three'
import { useThree, Camera, useFrame } from '@react-three/fiber'
import {
  Environment,
  SpotLight,
  CameraControlsImpl,
  Stats,
} from '@react-three/drei'
import { TrackballControls as TrackballControlsImpl } from 'three-stdlib'

import { Model } from '../CADModel'
import { CADLoading } from './CADLoading'
import { useAppStore, useCADPageStore } from '@/state'
import { CADEditorTip } from '../CADEditor/CADEditorTip'
import { GizmoHUD, GizmoViewcube } from '@/lib/cad'
import { CameraControls } from '../CADEditor/CameraControls'
import { useOrthographicViews } from '@/lib/cad/hooks/useOrthographicViews'

import hdrImage from '@/assets/potsdamer_platz_1k_micro.hdr?url'

interface SceneProps {
  onCameraRef?: (camera: Camera) => void
}

export const Scene = ({ onCameraRef }: SceneProps) => {
  const { camera } = useThree()
  const showAxis = useCADPageStore((state) => state.showAxis)
  const showStats = useAppStore((state) => state.showStatsPanel)
  const setCadPageState = useCADPageStore((state) => state.setState)
  const cameraControlsRef = useRef<CameraControlsImpl | null>(null)
  const trackballControlsRef = useRef<TrackballControlsImpl | null>(null)
  const { setRotationTo } = useOrthographicViews({
    cameraControlsRef,
    trackballControlsRef,
  })

  useEffect(() => {
    if (onCameraRef) {
      onCameraRef(camera)
    }
  }, [camera, onCameraRef])

  useFrame(({ size }) => {
    setCadPageState({
      canvasSize: { width: size.width, height: size.height },
    })
  })

  return (
    <>
      <Suspense fallback={<CADLoading />}>
        <CADEditorTip />
        <ambientLight intensity={0.8} />
        <Model />
        <axesHelper args={[0.2]} visible={showAxis} />
      </Suspense>
      <Environment files={hdrImage} />
      <SpotLight
        distance={50}
        intensity={100}
        angle={MathUtils.degToRad(45)}
        color={0xffffff}
        position={[0, -Math.PI, 0]}
        volumetric={false}
      />
      <CameraControls
        cameraControlsRef={cameraControlsRef}
        trackballControlsRef={trackballControlsRef}
      />
      <GizmoHUD alignment="bottom-left" margin={[80, 180]}>
        <GizmoViewcube
          onClick={setRotationTo}
          color="#e1e1e6"
          textColor="#000000"
        />
      </GizmoHUD>
      {showStats && <Stats />}
    </>
  )
}
